/**
 * A tiny virtual node model. Block renderers return VNodes so the same render
 * code produces DOM in the editor, DOM in the preview, DOM in the PDF service,
 * and HTML strings in tests — without any framework.
 */

export type VChild = VNode | string | number | null | undefined | false | VChild[];

export interface VNode {
  tag: string;
  props: VProps;
  children: Array<VNode | string>;
  /** "svg" for SVG namespace elements. Inferred from the tag when omitted. */
  ns?: "svg";
}

export interface VProps {
  class?: string | Array<string | false | null | undefined>;
  style?: string | Record<string, string | number | undefined>;
  /** Data attributes are passed as `data-*` keys. */
  [attr: string]: string | number | boolean | undefined | null | Array<string | false | null | undefined> | Record<string, string | number | undefined>;
}

const SVG_TAGS = new Set([
  "svg", "g", "path", "rect", "circle", "ellipse", "line", "polyline", "polygon", "text", "tspan", "defs", "clipPath", "linearGradient", "radialGradient", "stop", "use", "symbol", "mask", "pattern", "title", "desc", "foreignObject"
]);

export function h(tag: string, props?: VProps | null, ...children: VChild[]): VNode {
  const node: VNode = { tag, props: props ?? {}, children: [] };
  if (SVG_TAGS.has(tag)) node.ns = "svg";
  appendChildren(node.children, children);
  return node;
}

function appendChildren(out: Array<VNode | string>, children: VChild[]) {
  for (const c of children) {
    if (c === null || c === undefined || c === false || c === "") continue;
    if (Array.isArray(c)) appendChildren(out, c);
    else if (typeof c === "number") out.push(String(c));
    else out.push(c);
  }
}

export function isVNode(v: unknown): v is VNode {
  return !!v && typeof v === "object" && typeof (v as VNode).tag === "string" && Array.isArray((v as VNode).children);
}

export function classNames(value: VProps["class"]): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.filter(Boolean).join(" ");
}

export function styleString(value: VProps["style"]): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  return Object.entries(value)
    .filter(([, v]) => v !== undefined && v !== "" && v !== null)
    .map(([k, v]) => `${k}:${v}`)
    .join(";");
}

const ESC: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ESC[c]!);
}

const VOID = new Set(["area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta", "source", "track", "wbr"]);

/** Serialises a VNode tree to an HTML string. Attribute values are always escaped. */
export function toHtml(node: VNode | string | Array<VNode | string>): string {
  if (Array.isArray(node)) return node.map(toHtml).join("");
  if (typeof node === "string") return escapeHtml(node);
  const attrs: string[] = [];
  for (const [k, v] of Object.entries(node.props)) {
    if (v === undefined || v === null || v === false) continue;
    if (k === "class") {
      const c = classNames(v as VProps["class"]);
      if (c) attrs.push(`class="${escapeHtml(c)}"`);
    } else if (k === "style") {
      const s = styleString(v as VProps["style"]);
      if (s) attrs.push(`style="${escapeHtml(s)}"`);
    } else if (v === true) attrs.push(escapeHtml(k));
    else attrs.push(`${escapeHtml(k)}="${escapeHtml(String(v))}"`);
  }
  const open = `<${node.tag}${attrs.length ? " " + attrs.join(" ") : ""}>`;
  if (VOID.has(node.tag)) return open;
  return `${open}${node.children.map(toHtml).join("")}</${node.tag}>`;
}

/** Depth-first walk. Return false from the visitor to stop descending. */
export function walkVNodes(node: VNode, visit: (n: VNode) => boolean | void): void {
  if (visit(node) === false) return;
  for (const c of node.children) if (typeof c !== "string") walkVNodes(c, visit);
}
